// UserCommentCard — Iter160f (Randy pt-ytre-p2 / Wave 2).
//
// Iter160s update (Randy pt-ytre-family-mirror, Feb 2026) —
// Randy's mental model: a YouTube comment that already exists on the
// video (surfaced as an "existing comment" card here) is structurally
// the SAME thing as a silo_comment — both are children of a transcript
// slice. So this card gets the exact same footer treatment as
// SiloResponseCard, with the family-mirror rules:
//
//   * The Mold-Silo-Reaction picker + Length picker are FULLY INTERACTIVE
//     mirrors of the parent transcript's footer. Changing either here
//     updates the parent transcript + every sibling comment
//     immediately (single source of truth = `familyNudge[transcriptId]`
//     / `familyLength[transcriptId]` in YtreTab).
//   * A Re-draft button that overwrites THIS existing comment with a
//     fresh silo answer, using the family recipe.
//
// The card also gets the 3-stage palindromic chevron (Full ↔ Mid ↔
// Header-only), matching TranscriptCard + SiloResponseCard.

import { useState, useEffect, useRef } from "react";
import { CaretRight, CaretDown, ChatCircleDots, PencilSimple, Trash, ArrowsClockwise, MagicWand, Check, Eye, EyeSlash } from "@phosphor-icons/react";
import LengthStagePicker from "@/components/LengthStagePicker";
import TonePickerPill from "@/components/TonePickerPill";
import { useThreeStageChevron } from "@/hooks/useAnchoredCollapse";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import YTRedraftCapturePad from "@/components/YTRedraftCapturePad";
import GuidanceChip from "@/components/GuidanceChip";
import { readCardSettings, writeCardSettings } from "@/lib/cardSettingsStore";
import { useCardDraft } from "@/lib/useCardDraft";
import { draftAgeLabel } from "@/lib/ytreDraftCache";
import { renderWithMarks } from "@/lib/ytreSearchHighlight";

// Feb 27 2026 (Randy pt-ytre-card-settings-persist) — per-card tone /
// length / nudge selections are backed by localStorage via
// cardSettingsStore so they survive refresh (Randy's ask: "remembered
// for the specific card"). Keyed by entry.id → only persists FOR
// THAT card, not siblings.

function fmtTime(sec) {
  if (sec == null || sec < 0) return "";
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    : `${m}:${String(ss).padStart(2, "0")}`;
}

// Inline mini nudge stager — same UX shape as YtreTab's
// AskSiloNudgeStager but self-contained so this file has no cross-
// imports back into YtreTab. Simpler popover (no custom-create /
// custom-delete) since users have plenty of touch-points for that.
function InlineNudgeMirror({ nudges, staged, onStage, disabled, entryId }) {
  const [open, setOpen] = useState(false);
  const displayLabel = staged?.label || "Mold silo reaction";
  const isStaged = !!staged?.id;
  // Iter160u (Randy pt-dropdown-clip-fix) — use Radix Popover so this
  // menu portals to <body>, escaping the parent card's overflow-hidden
  // grid-collapse wrapper.
  //
  // Iter172 (Randy pt-ytre-per-card-mold, Feb 13, 2026) — this pill
  // is now PER-CARD (no family-mirror). Copy updated to reflect that.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          data-testid={`ytre-user-comment-nudge-btn-${entryId}`}
          title={isStaged
            ? `Mold silo reaction: ${displayLabel} — applies to THIS card's next re-draft only.`
            : "Pick a Mold silo reaction — applies to THIS card's next re-draft only."}
          className={`text-[11px] px-2.5 py-1.5 rounded-md border flex items-center gap-1 disabled:opacity-50 ${
            isStaged
              ? "border-[#4A5D4E] bg-[#EAF3EC] text-[#2F6B3A]"
              : "hairline text-[#4A5D4E] hover:bg-[#F0F1EE]"
          }`}
        >
          {displayLabel}
          {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-64 max-h-60 overflow-y-auto bg-white border hairline rounded-md shadow-md p-1.5 z-[80]"
        data-testid={`ytre-user-comment-nudge-popover-${entryId}`}
      >
        {(nudges || []).length === 0 && (
          <div className="px-2 py-2 text-[10.5px] text-[#6B6A68] italic">
            No nudges yet — add one from the YTRE Global Commands panel.
          </div>
        )}
        {(nudges || []).map((n) => {
          const isCurrent = staged?.id === n.id;
          return (
            <button
              key={n.id}
              onClick={() => {
                onStage({ id: n.id, label: n.name, is_custom: !n.is_global });
                setOpen(false);
              }}
              data-testid={`ytre-user-comment-nudge-opt-${entryId}-${n.id}`}
              className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 ${isCurrent ? "bg-[#F0F1EE]" : "hover:bg-[#F0F1EE]"}`}
            >
              <span className="text-[12px] font-medium text-[#1C1B1A] flex-1 truncate">{n.name}</span>
              {isCurrent && <span className="text-[10px] text-[#2F6B3A] font-medium">✓ staged</span>}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// 3-stage chevron state is inlined into the component below (rather than
// a shared hook) to keep this file self-contained.

export default function UserCommentCard({
  entry, busy, onSaveEdit, onDelete,
  nudges = [],
  siloSlug = null,
  siloId = null,           // needed by YTRedraftCapturePad for push-to-silo
  onRedraftFromUserComment = null,
  familyNudge = null,      // legacy (pre-iter170) family-mirror pill
  familyLength = null,     // legacy (pre-iter170) family-mirror length
  onStageFamilyNudge = null,
  onStageFamilyLength = null,
  // Iter170 §4-4 / §4-5 / §4-6 (Randy pt-ytre-owner-card-4-buttons):
  onApprove = null,        // fires on Approve — POSTs to /approve-and-post
  onRedraft = null,        // fires on Re-draft NOW — POSTs to /redraft
  // Sep 29 2026 (Randy pt-ytre-ignore-placeholder) — (entry, ignored) =>
  // POSTs to /ignore. Only rendered on a `Pending reply` placeholder.
  onIgnore = null,
  // Iter172 (Randy pt-ytre-refresh-and-minimize, Feb 13, 2026) —
  // parent-driven "minimize everything." Monotonic tick.
  minimizeSignal = 0,
  minimizeStage = 2,
  // Feb 16 2026 (Randy pt-ytre-recapture-persist) — family timestamp
  // (parent transcript's current time_end_sec) so Recapture on the
  // parent instantly relabels every child card in the family.
  familyTimestamp = null,
  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026) —
  // canvas-search props: `searchQuery` drives <mark> wrapping in the
  // body; `searchInfo` carries the active occurrence index within
  // THIS card; `revealTargetId`+`revealNonce` signal a stage-2 card
  // to expand to stage-1 so the match is visible.
  searchQuery = "",
  searchInfo = null,
  revealTargetId = null,
  revealNonce = 0,
}) {
  // Iter172 audit-6.1 (Randy pt-ytre-edit-match-full-height, Feb 13,
  // 2026) — captures the DISPLAY block's rendered height the instant
  // Edit is tapped. Applied as textarea minHeight so Edit mode opens
  // at exactly the same size the reader was looking at, not clamped
  // to a rows-based estimate. Solves the "why is my full-open card
  // shrinking to 30 rows when I hit Edit" complaint.
  const displayRef = useRef(null);
  const [editStartHeight, setEditStartHeight] = useState(null);
  // Feb 27 2026 (Randy pt-ytre-card-settings-persist) — hydrate from
  // localStorage on first mount so tone/length/nudge selections survive
  // refresh. Randy: "settings chosen when creating a re-draft ... would
  // like those to be remembered for the specific card."
  const _saved = readCardSettings(entry.id);
  // Iter170 §4-4 — per-card independent Length (no more family-mirror).
  const [stagedLength, setStagedLength] = useState(_saved?.length_id || entry.length_preset_id || familyLength || null);
  // Iter172 (Randy pt-ytre-per-card-mold, Feb 13, 2026) — per-card
  // Mold silo reaction. Now hydrates from localStorage-backed
  // nudge_id when available. Falls back to legacy family cascade.
  const [stagedNudge, setStagedNudge] = useState(() => {
    if (_saved?.nudge_id && Array.isArray(nudges)) {
      const found = nudges.find((n) => (n.id || n.command_id) === _saved.nudge_id);
      if (found) return found;
    }
    return familyNudge || null;
  });
  const [nudgePopoverOpen, setNudgePopoverOpen] = useState(false);
  // Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026) — per-card TONE
  // DELIVERY (YTRE-only). Hydrates from localStorage — TonePickerPill
  // renders label + swatch by looking up `staged.id` in its own tone
  // registry, so we only need to preserve the id.
  const [stagedTone, setStagedTone] = useState(_saved?.tone_id ? { id: _saved.tone_id } : null);
  // Persist each stager change to localStorage. Skip the initial null
  // seed so we don't create empty entries for cards the user never
  // touched. (Empty-object writes are also guarded inside the helper.)
  useEffect(() => { writeCardSettings(entry.id, { length_id: stagedLength || null }); }, [entry.id, stagedLength]);
  useEffect(() => { writeCardSettings(entry.id, { nudge_id: (stagedNudge?.id || stagedNudge?.command_id) || null }); }, [entry.id, stagedNudge]);
  useEffect(() => { writeCardSettings(entry.id, { tone_id: stagedTone?.id || null }); }, [entry.id, stagedTone]);
  // Iter170 §4-6 corrected flow (Randy Feb 12) — two-stage redraft:
  //   Stage 1: tap "Re-draft" → opens YTRedraftCapturePad → user
  //            captures new info, pushes to silo OR just closes.
  //   Stage 2: on pad close, button changes to "Re-draft now" (green).
  //            Tap "Re-draft now" → executes /redraft against silo with
  //            freshly-updated memory + Dialogue > Silo > Transcript.
  const [padOpen, setPadOpen] = useState(false);
  const [redraftArmed, setRedraftArmed] = useState(false);
  // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — guidance captured
  // by the pad's "Guide this reply" action. Now an object
  //   { text: string, persist: boolean }
  // so the card knows whether to clear after next re-draft (one-shot)
  // or keep it armed for future re-drafts (sticky per Randy's revised
  // ask). Chip renders when set; click chip to edit, X to clear.
  const [padGuidance, setPadGuidance] = useState(null);
  const [approving, setApproving] = useState(false);
  const [redrafting, setRedrafting] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  // Local 3-stage palindromic chevron. Same triangle-wave as YtreTab's
  // useThreeStageChevron. Duplicated here to keep this file self-
  // contained (no back-references to YtreTab).
  // Iter160t (Randy pt-ytre-chevron-no-jump, Feb 2026) — added anchor-
  // scroll compensation so cycling the chevron never yanks the viewport.
  // Capture the card's top offset BEFORE the state change; after React
  // commits + the transition starts, measure the delta and scrollBy to
  // pin the card in place.
  // Iter160t — 3-stage palindromic chevron from the shared platform hook.
  // See /app/frontend/src/hooks/useAnchoredCollapse.js for the full
  // spec (anchor-scroll compensation + StrictMode-safe updater).
  const chev = useThreeStageChevron({ initialStage: 1 });
  // Sep 30, 2026 (Randy pt-ytre-draft-autosave) — a half-typed comment edit
  // used to die with the unmount. Mirrored to localStorage now; `recovered`
  // is only set when the stored text still differs from the saved row.
  const dr = useCardDraft({
    id: entry.id, baseline: entry.transcript_raw || "", field: "transcript_raw",
    onStart: () => {
      if (displayRef.current) setEditStartHeight(displayRef.current.offsetHeight);
      chev.setStage(0);
    },
  });
  // Iter172 audit-3 (Randy pt-ytre-minimize-all, Feb 13, 2026) — parent
  // "Minimize all" button drives EVERY card to header-only (was: every
  // card except the latest-interacted, per audit-2). Skip the initial 0
  // so freshly-mounted cards don't auto-collapse on first render.
  useEffect(() => {
    if (minimizeSignal > 0) chev.setStage(minimizeStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimizeSignal]);
  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026) —
  // when this card is the search's reveal target AND currently in
  // stage-2 (header-only), expand to stage-1 so the match is visible.
  // If already stage-0 or stage-1, leave it alone (no forced expand).
  useEffect(() => {
    if (revealNonce > 0 && revealTargetId === entry.id && chev.isHeaderOnly) {
      chev.setStage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce]);
  const isFull = chev.isFull;
  const isHeaderOnly = chev.isHeaderOnly;
  const cardRef = chev.cardRef;
  const stage = chev.stage;

  // Oct 6 2026 (Randy — YTRE white screen) — a stray effect lived here:
  //     useEffect(() => { if (!editing) setDraft(entry.transcript_raw || ""); },
  //              [entry.transcript_raw, editing]);
  // It belonged to the local `const [editing] / const [draft]` state that sat
  // ABOVE this line until the Sep 30 draft-autosave refactor (57fcab4) replaced
  // both with useCardDraft. That refactor put the destructure
  //     const { editing, draft, setDraft, recovered } = dr;
  // ~100 lines BELOW this point, so the effect's dependency array read a `const`
  // in its temporal dead zone: "Cannot access 'editing' before initialization",
  // thrown on EVERY render of this card — a hard white screen the moment a
  // canvas contained a user-comment entry.
  // Deleted rather than moved: it is dead code. `draft` is only ever read while
  // editing (see the editing ? textarea : display branch below), useCardDraft's
  // start() already seeds it from baseline, and revertToSaved() covers reverting
  // to the saved text. Re-syncing a draft that nothing is displaying could only
  // ever clobber a recovered one.

  // Feb 16 2026 (Randy pt-ytre-recapture-persist) — family timestamp
  // priority: prefer the PARENT transcript's current time_end_sec (via
  // the `familyTimestamp` prop) so a manual Recapture immediately
  // relabels every child card in the family. Fall back to the child's
  // frozen anchor_sec for pre-recapture / no-parent cases.
  const anchor = (typeof familyTimestamp === "number" && familyTimestamp >= 0)
    ? familyTimestamp
    : entry.anchor_sec;
  const hasTimestamp = typeof anchor === "number" && anchor >= 0;
  const timeLabel = hasTimestamp ? `@ ${fmtTime(anchor)}` : "no timestamp";
  const badgeClass = hasTimestamp
    ? "text-[10px] px-1.5 py-0.5 rounded bg-[#EAF3EC] text-[#2F6B3A] font-mono"
    : "text-[10px] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] italic";

  const replyCount = entry.reply_count || 0;

  // Sep 29 2026 (Randy pt-ytre-ignore-placeholder) — the blank placeholder
  // YTRE writes for a new viewer reply is the ONLY card Ignore applies to:
  // an owner turn with no author, nothing posted, and the untouched
  // "Pending reply" body (or one already parked as Ignore).
  const isIgnored = entry.ignored === true;
  const isPendingPlaceholder = (
    entry.is_owner_turn === true
    && !entry.author_display_name
    && entry.post_state !== "posted"
    && (isIgnored || (entry.transcript_raw || "").trim() === "Pending reply")
  );

  // Iter169-Phase3 (Randy pt-ytre-threaded-dialogue, Feb 2026) —
  // per-turn card semantics driven by `entry.is_owner_turn`.
  //  - undefined (legacy blob card) → "Existing comment · N replies"
  //  - true (owner turn, new split)  → "comment · N replies · @handle"
  //  - false (viewer turn, new split) → "replies · @handle" + read-only,
  //    NO family footer at all (only the top-right edit/trash icons).
  const isPerTurnCard = typeof entry.is_owner_turn === "boolean";
  const isOwnerTurn = entry.is_owner_turn === true;
  const isViewerTurn = entry.is_owner_turn === false;
  const authorHandle = entry.author_display_name || "";
  const isPosted = entry.post_state === "posted";

  // Sep 15 2026 (Randy pt-ytre-glow-means-new-reply) — the green glow used
  // to mean "an Approve button is waiting inside this collapsed card",
  // which landed it on the blank "Pending reply" placeholder YTRE inserts
  // for him to answer into. His screenshot: "it shouldn't even count as a
  // reply 'at all' because it's a placeholder for the interaction agent…
  // thus counting it as the 'new reply' which is wrong."
  //
  // Now it means one thing only: a real named author, who is NOT the
  // video's interaction agent, replied since he last opened this video.
  // The backend decides that (`is_new_inbound_reply`) so the canvas and
  // the alert banner can never disagree. Placeholders carry no author and
  // are owner turns, so they can never qualify. Shown at every chevron
  // stage, not just collapsed — a new reply is worth seeing while reading.
  // Sep 16 2026 (Randy pt-ytre-glow-guard) — on PRODUCTION the rim appeared
  // on his blank "Pending reply" placeholder, while the same URL rendered
  // correctly in preview. I could not reproduce it from source: the flag is
  // written in three per-turn loops that each already require an author, and
  // production was verified to be running the same commit. So rather than
  // trust the stored value, the render re-checks the structural facts. A
  // placeholder is an owner turn with no author and no posted state, so it
  // is now incapable of glowing no matter what the database says.
  const isNewInboundReply = (
    entry.is_new_inbound_reply === true
    && !!authorHandle          // "from a person who has a name"
    && isPosted                // a real YouTube reply, not a local draft
    && isPerTurnCard && !isOwnerTurn   // and not the interaction agent's own
  );

  // Iter172 audit-4 side-quest (Randy pt-ytre-thread-labeling, Feb 13,
  // 2026) — was: every owner turn read "comment · 7 replies" even when
  // it was actually Randy's reply within the thread (positions 3, 5, 7).
  // Randy screenshot showed a Randy→Mark→Randy→Mark dialogue rendering
  // as if he'd posted 4 separate top-level comments. Fix: only the
  // top-level owner turn (thread_position === 0) reads "comment · N
  // replies"; owner replies within the thread now read "reply".
  //  - OWNER top-level (pos 0): `comment · 4 replies · @ 3:57 · POSTED · @EastTactics`
  //  - OWNER reply    (pos 1+): `reply · @ 3:57 · POSTED · @EastTactics`
  //  - VIEWER:                   `reply · @ 3:57 · POSTED · @anthonym2499`
  //  - LEGACY blob:              `Existing comment · 4 replies` (unchanged)
  const isThreadTopLevel = isPerTurnCard && entry.thread_position === 0;
  const threadLabel = isPerTurnCard
    ? (isThreadTopLevel
        ? (replyCount > 0
            ? `comment · ${replyCount} ${replyCount === 1 ? "reply" : "replies"}`
            : "comment")
        : "reply")
    : (replyCount > 0
        ? `Existing comment · ${replyCount} ${replyCount === 1 ? "reply" : "replies"}`
        : "Existing comment");

  // Iter172 audit-6.1 (Randy pt-ytre-edit-match-full-height, Feb 13, 2026) —
  // the DISPLAY block's rendered height is captured in the hook's onStart,
  // BEFORE the toggle to edit mode, so the textarea opens at exactly the
  // visual size the reader was looking at. Randy's ask: "if I have it
  // maximized to the full extent, then choose Edit, it should open the edit
  // mode and still have the full entire text area."
  const { editing, draft, setDraft, recovered } = dr;
  const startEdit = dr.start;
  const cancelEdit = dr.discard;
  const commitEdit = () => dr.commit((text) => onSaveEdit({ transcript_raw: text }));

  // Re-draft footer only makes sense when this comment is paired to a
  // transcript slice (so we have SOMETHING to feed into ask-silo).
  const canRedraft = !!(entry.source_entry_id && onRedraftFromUserComment);
  const nudgeStaged = !!familyNudge?.id;
  const lengthStaged = !!familyLength;
  const redraftReady = nudgeStaged && lengthStaged;
  const redraftDisabledTitle = !nudgeStaged && !lengthStaged
    ? "Pick a Mold Silo Reaction and a Length (they mirror across every card in this family)."
    : !nudgeStaged
      ? "Pick a Mold Silo Reaction (mirrors across every card in this family)."
      : "Pick a Length (mirrors across every card in this family).";

  return (
    <div
      ref={cardRef}
      data-ytre-entry-id={entry.id}
      className={`border border-t-0 hairline rounded-t-none rounded-b-lg bg-white ${
        isViewerTurn ? "ml-8" : ""
      } ${isNewInboundReply ? "ytre-new-reply-glow" : ""}`}
      data-testid={`ytre-card-user-comment-${entry.id}`}
      data-owner-turn={isPerTurnCard ? String(isOwnerTurn) : "legacy"}
      data-new-reply={isNewInboundReply ? "true" : "false"}
    >
      <div className={`flex items-center justify-between px-3 py-2 border-b hairline ${
        isViewerTurn ? "bg-[#F0F1EE]" : "bg-[#F7FAF7]"
      }`}
      data-testid={`ytre-user-comment-header-${entry.id}`}
      data-approve-pending={isPerTurnCard && isOwnerTurn && !isPosted ? "true" : "false"}
      >
        <button
          onClick={chev.cycle}
          data-testid={`ytre-user-comment-collapse-${entry.id}`}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
          title={`Chevron cycles: full ↔ mid ↔ header-only (stage ${stage + 1}/3)`}
        >
          {isFull
            ? <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
            : isHeaderOnly
              ? <CaretRight size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
              : <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0 opacity-60" />}
          <ChatCircleDots size={13} weight="duotone" className={`shrink-0 ${
            isViewerTurn ? "text-[#8A6A3A]" : "text-[#4A8060]"
          }`} />
          <span className="text-[11.5px] font-medium text-[#1C1B1A] truncate">{threadLabel}</span>
          <span className={badgeClass} data-testid={`ytre-user-comment-anchor-${entry.id}`}>{timeLabel}</span>
          {/* Iter169-Phase3 — POSTED badge for turns already live on YouTube.
              Owner draft cards (auto-drafted by /refresh-replies) will NOT
              have post_state="posted" yet, so this badge is honest. */}
          {isPosted && (
            <span
              data-testid={`ytre-user-comment-posted-${entry.id}`}
              className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#EAF3EC] text-[#2F6B3A] font-medium"
            >
              POSTED
            </span>
          )}
          {authorHandle && (
            <span
              data-testid={`ytre-user-comment-author-${entry.id}`}
              className="text-[10.5px] text-[#4A5D4E] italic truncate"
              title={authorHandle}
            >
              {authorHandle}
            </span>
          )}
          {entry.user_edited && (
            <span className="text-[9.5px] uppercase tracking-[0.14em] text-[#8A6A3A] italic ml-1">edited</span>
          )}
          {isIgnored && (
            <span
              data-testid={`ytre-user-comment-ignored-${entry.id}`}
              className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] font-medium"
              title="Ignored — this comment is classified as handled and raises no alert."
            >
              IGNORED
            </span>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {!editing && (
            <button
              onClick={startEdit}
              data-testid={`ytre-user-comment-edit-${entry.id}`}
              className="p-1 rounded hover:bg-white"
              title="Edit"
            >
              <PencilSimple size={12} weight="duotone" />
            </button>
          )}
          <button
            onClick={onDelete}
            data-testid={`ytre-user-comment-delete-${entry.id}`}
            className="p-1 rounded hover:bg-[#F5E5E1] text-[#C16E5A]"
            title="Delete"
          >
            <Trash size={12} weight="duotone" />
          </button>
        </div>
      </div>

      {/* Body — Iter160t animated grid-template-rows collapse. */}
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: (editing || !isHeaderOnly) ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
          {editing ? (
            <div className="p-3">
              {recovered && (
                <div
                  className="mb-2 flex items-center gap-2 text-[11px] text-[#8A6A3A] bg-[#FBF6EC] border border-[#E6D9BE] rounded px-2 py-1.5"
                  data-testid={`ytre-draft-recovered-${entry.id}`}
                >
                  <span className="flex-1">
                    Unsaved edit restored — you typed this {draftAgeLabel(recovered.at)} and never hit Save.
                  </span>
                  <button
                    type="button"
                    onClick={dr.revertToSaved}
                    data-testid={`ytre-draft-revert-${entry.id}`}
                    className="underline hover:no-underline"
                  >
                    Throw it away
                  </button>
                </div>
              )}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(30, Math.max(8, (draft || "").split("\n").length + 3))}
                style={editStartHeight ? { minHeight: `${editStartHeight}px` } : undefined}
                className="w-full text-[12.5px] leading-relaxed border hairline rounded-md px-3 py-2 font-mono bg-white focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                data-testid={`ytre-user-comment-textarea-${entry.id}`}
              />
              <div className="flex items-center justify-end gap-2 mt-2">
                <button
                  onClick={cancelEdit}
                  className="text-[11px] px-3 py-1 rounded-md border hairline hover:bg-[#F0F1EE]"
                  data-testid={`ytre-user-comment-cancel-${entry.id}`}
                >
                  Cancel
                </button>
                <button
                  onClick={commitEdit}
                  className="text-[11px] px-3 py-1 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E]"
                  data-testid={`ytre-user-comment-save-${entry.id}`}
                >
                  Save
                </button>
              </div>
            </div>
          ) : isFull ? (
            <div className="p-3">
              <div
                ref={displayRef}
                className="text-[12.5px] leading-relaxed text-[#1C1B1A] whitespace-pre-wrap font-mono"
                data-testid={`ytre-user-comment-body-${entry.id}`}
              >
                {entry.transcript_raw
                  ? (searchQuery
                      ? renderWithMarks(entry.transcript_raw, searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                      : entry.transcript_raw)
                  : <span className="text-[#6B6A68] italic">(empty comment)</span>}
              </div>
            </div>
          ) : (
            <div
              ref={displayRef}
              className="p-3 text-[12px] text-[#1C1B1A] cursor-pointer leading-relaxed font-mono"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              onClick={chev.cycle}
              data-testid={`ytre-user-comment-preview-${entry.id}`}
              title="Click to cycle chevron"
            >
              {searchQuery && entry.transcript_raw
                ? renderWithMarks(entry.transcript_raw, searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                : (entry.transcript_raw || "(empty comment)")}
            </div>
          )}
        </div>
      </div>

      {/* Footer — Iter170 §4-4 (Randy pt-ytre-owner-card-4-buttons,
          Feb 2026). Three render paths:
            1. isPerTurnCard && isOwnerTurn → NEW 4-button footer
               (Approve / Edit / Re-draft / Length), NO family-mirror
            2. isPerTurnCard && isViewerTurn → NO footer (viewer replies
               are read-only records; edit/trash icons live in header)
            3. !isPerTurnCard (legacy blob card) → LEGACY family-mirror
               footer preserved for backward-compat with old sessions
      */}
      {isPerTurnCard && isOwnerTurn && !editing && !isHeaderOnly && (
        <div
          className="px-3 py-2 border-t hairline bg-[#F4F8F5] flex items-center gap-2 flex-wrap"
          data-testid={`ytre-user-comment-owner-footer-${entry.id}`}
        >
          {/* 1 · Approve (draft) | Approved chip (posted+clean) | Re-post (posted+dirty)
              Feb 16 2026 (Randy pt-ytre-repost) — after Redraft or manual
              Edit on a posted card, the chip flips to "Re-post" and
              tapping it opens a confirm modal that overwrites the live
              YouTube comment via comments.update (same YT id preserved).
              Feb 17 2026 (Randy pt-ytre-repost-fix) — dirty-check no
              longer requires last_posted_body != null. Backend now
              backfills the snapshot on the first redraft/edit of a
              posted card, so this comparison works for both new posts
              AND legacy posts (pre Feb 16). Fallback of `?? current`
              means "clean" for legacy cards that have NEVER been
              redrafted or edited since posting — which is the correct
              default (their local body still matches YouTube). */}
          {/* Sep 29 2026 (Randy pt-ytre-ignore-placeholder) — an ignored
              placeholder must never offer Approve: posting the word "Ignore"
              to YouTube is the one action that can't be taken back. */}
          {isIgnored && (
            <span
              data-testid={`ytre-user-comment-ignored-pill-${entry.id}`}
              className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#F0F1EE] text-[#6B6A68] border hairline flex items-center gap-1"
              title="You chose to ignore this comment. Nothing was posted to YouTube and its new-reply alert is retired. Edit or Re-draft to turn it into a real reply."
            >
              <EyeSlash size={11} weight="duotone" />
              Ignored
            </span>
          )}
          {!isIgnored && (() => {
            const currentBody = (entry.transcript_revised || entry.transcript_raw || "").trim();
            const baselineBody = (entry.last_posted_body != null
              ? entry.last_posted_body
              : currentBody).trim();
            const bodyIsDirty = isPosted && currentBody !== baselineBody;
            if (isPosted && !bodyIsDirty) {
              return (
                <span
                  data-testid={`ytre-user-comment-approved-pill-${entry.id}`}
                  className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#EAF3EC] text-[#2F6B3A] border border-[#4A5D4E] flex items-center gap-1"
                  title="This reply is live on YouTube. Editing the body flips it to Re-post."
                >
                  <Check size={11} weight="bold" />
                  Approved
                </span>
              );
            }
            const isRepost = isPosted && bodyIsDirty;
            const label = isRepost ? (approving ? "Re-posting…" : "Re-post") : (approving ? "Posting…" : "Approve");
            const hoverTitle = isRepost
              ? "Overwrite the live YouTube reply with the current text."
              : "Post this reply to YouTube using this silo's connected channel.";
            return (
              <button
                onClick={async () => {
                  if (!onApprove || approving) return;
                  if (isRepost) {
                    // eslint-disable-next-line no-alert
                    const ok = window.confirm("Overwrite the live YouTube reply?");
                    if (!ok) return;
                  }
                  setApproving(true);
                  try { await onApprove(entry); }
                  finally { setApproving(false); }
                }}
                disabled={approving || busy || !onApprove}
                data-testid={`ytre-user-comment-${isRepost ? "repost" : "approve"}-btn-${entry.id}`}
                title={hoverTitle}
                className={`text-[11.5px] px-3 py-1.5 rounded-md text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${isRepost ? "bg-[#C16E5A] hover:bg-[#A85A47]" : "bg-[#2F6B3A] hover:bg-[#3A4A3E]"}`}
              >
                <Check size={11} weight="bold" />
                {label}
              </button>
            );
          })()}

          {/* Ignore / Un-ignore — only ever on a `Pending reply` placeholder.
              Randy: "if the user chooses to reply, that becomes the path. If
              the user chooses to ignore, the words 'pending reply' just
              change to ignore." */}
          {isPendingPlaceholder && onIgnore && (
            <button
              onClick={async () => {
                if (ignoring) return;
                setIgnoring(true);
                try { await onIgnore(entry, !isIgnored); } finally { setIgnoring(false); }
              }}
              disabled={ignoring || busy}
              data-testid={`ytre-user-comment-ignore-btn-${entry.id}`}
              title={isIgnored
                ? "Put this card back to Pending reply."
                : "Ignore this comment — the card reads Ignore, nothing is posted, and its new-reply alert retires for good."}
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1"
            >
              {isIgnored ? <Eye size={11} weight="duotone" /> : <EyeSlash size={11} weight="duotone" />}
              {ignoring ? "Saving…" : isIgnored ? "Un-ignore" : "Ignore"}
            </button>
          )}

          {/* 2 · Edit */}
          <button
            onClick={startEdit}
            disabled={busy}
            data-testid={`ytre-user-comment-edit-btn-${entry.id}`}
            title="Edit this reply's body inline. Editing a POSTED card flips it back to draft."
            className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1"
          >
            <PencilSimple size={11} weight="duotone" />
            Edit
          </button>

          {/* Iter172 (Randy pt-ytre-per-card-mold, Feb 13, 2026) —
              per-card Mold silo reaction pill. Sits BETWEEN Edit and
              Re-draft per Randy's spec. Local React state → resets
              on session change. Sends `nudge_label` up on redraft so
              the backend's `_build_child_context_prompt` fires
              REACTION MODE for THIS card's re-draft only. */}
          <InlineNudgeMirror
            nudges={nudges}
            staged={stagedNudge}
            onStage={(n) => { setStagedNudge(n); setNudgePopoverOpen(false); }}
            disabled={busy}
            entryId={`usercomment-mold-${entry.id}`}
            open={nudgePopoverOpen}
            onOpenChange={setNudgePopoverOpen}
          />

          {/* Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026) — YTRE-only
              Tone delivery picker sits IMMEDIATELY right of the Mold
              silo reaction pill (Randy's spec). Default OFF — no
              directive injected unless the user picks a tone. */}
          <TonePickerPill
            staged={stagedTone}
            onStage={setStagedTone}
            disabled={busy}
            entryId={`usercomment-${entry.id}`}
          />

          {/* 3 · Re-draft — Iter170 §4-6 corrected flow (Randy Feb 12).
              Stage 1 (unarmed): "Re-draft" gray outline → tap opens
              YTRedraftCapturePad for Capture-new-info-for-the-silo.
              Stage 2 (armed after pad closes): "Re-draft now" green
              filled → tap runs /redraft. On success button unarms. */}
          <button
            onClick={() => {
              if (redraftArmed) {
                // Stage 2 — execute the re-draft. Iter172 passes the
                // card's staged nudge_label so REACTION MODE fires.
                if (!onRedraft || redrafting) return;
                setRedrafting(true);
                // Feb 27 2026 (Randy pt-ytre-guide-this-reply revised)
                // — pass the guidance TEXT into the LLM. Clear the
                // state ONLY when the user didn't check "keep for
                // future re-drafts" (padGuidance.persist=false, the
                // default). Sticky guidance stays armed until the
                // user edits or clears from the chip.
                const _molding = padGuidance?.text || null;
                if (padGuidance && !padGuidance.persist) setPadGuidance(null);
                Promise.resolve(onRedraft(entry, {
                  length_pref: stagedLength,
                  nudge_label: stagedNudge?.name || stagedNudge?.label || null,
                  // Iter172 (Randy pt-ytre-tone-picker) — send the
                  // card's staged Tone id (null when default/off).
                  tone_id: stagedTone?.id || null,
                  ...(_molding ? { molding_hint: _molding } : {}),
                }))
                  .catch(() => {})
                  .finally(() => { setRedrafting(false); setRedraftArmed(false); });
              } else {
                // Stage 1 — open the assist pad
                setPadOpen(true);
              }
            }}
            disabled={busy || redrafting || !onRedraft}
            data-testid={`ytre-user-comment-redraft-btn-${entry.id}`}
            data-armed={String(redraftArmed)}
            title={redraftArmed
              ? "Run the re-draft now (silo picks up any info you just pushed)"
              : "Open the Re-draft assist pad — capture new info for the silo, then run the re-draft"}
            className={redraftArmed
              ? "text-[11.5px] px-3 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1"
              : "text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1"}
          >
            {redrafting
              ? <ArrowsClockwise size={11} weight="duotone" className="animate-spin" />
              : redraftArmed
                ? <Check size={11} weight="bold" />
                : <ArrowsClockwise size={11} weight="duotone" />}
            {redrafting ? "Re-drafting…" : redraftArmed ? "Re-draft now" : "Re-draft"}
          </button>

          {/* Feb 27 2026 (Randy pt-ytre-guide-chip) — visible chip so
              Randy can *see* his guidance is armed. Click chip body to
              edit; X to clear. Rendered only when guidance is set. */}
          <GuidanceChip
            guidance={padGuidance}
            onEdit={() => setPadOpen(true)}
            onClear={() => setPadGuidance(null)}
            testId={`usercomment-${entry.id}`}
          />

          {/* 4 · Length (per-card, independent). Iter172 — moved to
              FAR RIGHT via ml-auto to mirror the transcript card's
              Length placement. Blue styling to match transcript's
              Length pill (visual family). */}
          <div className="ml-auto" data-testid={`usercomment-length-anchor-${entry.id}`}>
            <LengthStagePicker
              slug={siloSlug}
              staged={stagedLength}
              onStage={setStagedLength}
              disabled={busy}
              entryId={`usercomment-${entry.id}`}
              promptLabel="Length for THIS card's next re-draft (independent — does not affect siblings)"
            />
          </div>
        </div>
      )}

      {/* Iter170 §4-6 corrected — reuse YTRedraftCapturePad (pure UI
          component, no pipeline surface). On close, the pad calls
          onClose(didPush). Either push OR close arms the "Re-draft now"
          button. Per Randy: "then after the push to silo, or choose to
          close the pop up. the re-draft button on the comment footer
          then changes to 'Re-draft now'." */}
      {isPerTurnCard && isOwnerTurn && padOpen && (
        <YTRedraftCapturePad
          siloId={siloId}
          initialText={padGuidance?.text || ""}
          initialPersist={!!padGuidance?.persist}
          onClose={(_didPush, guidance, persist) => {
            setPadOpen(false);
            setRedraftArmed(true);
            if (guidance && guidance.trim()) {
              setPadGuidance({ text: guidance.trim(), persist: !!persist });
            }
          }}
        />
      )}

      {/* LEGACY family-mirror footer — preserved for pre-iter170 sessions
          (blob-shape user_comment entries without is_owner_turn). Do
          not touch — this path exists purely for backward-compat. */}
      {!isPerTurnCard && (
        <div
          className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
          style={{ gridTemplateRows: (!editing && !isHeaderOnly && canRedraft) ? "1fr" : "0fr" }}
        >
          <div className="min-h-0">
            <div className="px-3 py-2 border-t hairline bg-[#F4F8F5] flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68]" title="Legacy family-mirror footer (pre-iter170 sessions).">
                Family:
              </span>
              <InlineNudgeMirror
                nudges={nudges}
                staged={familyNudge}
                onStage={(nudge) => onStageFamilyNudge && onStageFamilyNudge(nudge)}
                disabled={false}
                entryId={entry.id}
              />
              <LengthStagePicker
                slug={siloSlug}
                staged={familyLength}
                onStage={(id) => onStageFamilyLength && onStageFamilyLength(id)}
                disabled={false}
                entryId={`usercomment-${entry.id}`}
                promptLabel="Stage length for the family — every card mirrors it"
              />
              <button
                onClick={() => {
                  if (!redraftReady) return;
                  onRedraftFromUserComment(entry, {
                    nudgeId: familyNudge?.id,
                    length: familyLength,
                  });
                }}
                disabled={busy || !redraftReady}
                data-testid={`ytre-user-comment-redraft-${entry.id}`}
                className="ml-auto text-[11.5px] px-3 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                title={redraftReady
                  ? "Re-draft this comment using the family's staged Mold Silo Reaction + Length. Overwrites this card only."
                  : redraftDisabledTitle}
              >
                {redraftReady ? <MagicWand size={11} weight="fill" /> : <ArrowsClockwise size={11} weight="duotone" />}
                Re-draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
